package com.example.readingnotes.util

import com.example.readingnotes.data.remote.api.ApiException

/**
 * エラータイプの分類
 */
enum class ErrorType {
    NETWORK, // ネットワークエラー（接続失敗、タイムアウト）
    CLIENT, // クライアントエラー（4xx）
    SERVER, // サーバーエラー（5xx）
    VALIDATION, // バリデーションエラー
    AUTH, // 認証エラー
    UNKNOWN, // 不明なエラー
}

/**
 * エラーメッセージを提供するユーティリティ
 * Requirements 5.1, 5.2, 5.3に対応
 */
object ErrorMessageProvider {
    /**
     * エラーからエラータイプを判定する
     */
    fun getErrorType(error: Throwable): ErrorType =
        when (error) {
            is ApiException.NetworkException -> ErrorType.NETWORK
            is ApiException.ServerException -> ErrorType.SERVER
            is ApiException.ValidationException -> ErrorType.VALIDATION
            is ApiException.UnauthorizedException -> ErrorType.AUTH
            is ApiException.ForbiddenException -> ErrorType.AUTH
            is ApiException.NotFoundException -> ErrorType.CLIENT
            is ApiException.ClientException -> ErrorType.CLIENT
            is ApiException.UnknownException -> ErrorType.UNKNOWN
            else -> ErrorType.UNKNOWN
        }

    /**
     * エラーからローカライズされたメッセージを取得する
     *
     * @param error 発生したエラー
     * @param fallbackMessage APIからのメッセージがない場合のフォールバック
     * @return 日本語のエラーメッセージ
     */
    fun getLocalizedMessage(error: Throwable, fallbackMessage: String? = null): String =
        when (error) {
            is ApiException.NetworkException ->
                "接続エラーが発生しました。ネットワークを確認してください。"

            is ApiException.ServerException ->
                "サーバーエラーが発生しました。しばらく後に再試行してください。"

            is ApiException.ValidationException ->
                error.message ?: "入力内容に誤りがあります。"

            is ApiException.UnauthorizedException ->
                "認証が必要です。ログインしてください。"

            is ApiException.ForbiddenException ->
                translateForbiddenMessage(error.message) ?: "アクセス権限がありません。"

            is ApiException.NotFoundException ->
                "リソースが見つかりません。"

            is ApiException.ClientException ->
                error.message ?: "リクエストに問題があります。入力内容を確認してください。"

            is ApiException.UnknownException ->
                "予期しないエラーが発生しました。"

            else -> {
                // For non-ApiException errors, use the original message if available
                // This preserves error messages from the API that aren't wrapped in ApiException
                error.message?.takeIf { it.isNotBlank() }
                    ?: fallbackMessage
                    ?: "予期しないエラーが発生しました。"
            }
        }

    /**
     * エラータイプに応じたデフォルトメッセージを取得する
     */
    fun getDefaultMessage(errorType: ErrorType): String =
        when (errorType) {
            ErrorType.NETWORK -> "接続エラーが発生しました。ネットワークを確認してください。"
            ErrorType.CLIENT -> "リクエストに問題があります。入力内容を確認してください。"
            ErrorType.SERVER -> "サーバーエラーが発生しました。しばらく後に再試行してください。"
            ErrorType.VALIDATION -> "入力内容に誤りがあります。"
            ErrorType.AUTH -> "認証が必要です。ログインしてください。"
            ErrorType.UNKNOWN -> "予期しないエラーが発生しました。"
        }

    /**
     * 権限エラーメッセージを日本語に変換する
     */
    private fun translateForbiddenMessage(message: String?): String? {
        if (message == null) return null
        return when {
            message.contains("edit your own posts", ignoreCase = true) ->
                "自分の投稿のみ編集できます。"
            message.contains("delete your own posts", ignoreCase = true) ->
                "自分の投稿のみ削除できます。"
            message.contains("Admin access required", ignoreCase = true) ->
                "管理者権限が必要です。"
            message == "アクセス権限がありません" -> null // Use default
            else -> null
        }
    }
}
